/**
 * ConfirmReceivedForm — the one "record a payment as received" row (sweep 2
 * D6; previously three hand-rolled copies: client deposit, client balance,
 * and each contractor payout).
 *
 * Owns its date state (defaults to today) and validity; the caller supplies
 * the labels, the pending flag, any external disables (busy, invalid hours),
 * and the confirm handler, which receives the ISO date.
 */
import { useState } from "react";
import { cx } from "@shared/cx";
import { Button } from "../ui/Button";
import { DateInputNZ } from "../ui/DateInputNZ";
import forms from "../ui/field.module.css";
import { InlineBanner } from "../ui/InlineBanner";
import styles from "./ConfirmReceivedForm.module.css";

const today = (): string => new Date().toISOString().split("T")[0];

interface ConfirmReceivedFormProps {
  /** e.g. "Date received" / "Date paid" */
  dateLabel: string;
  dateAriaLabel: string;
  /** e.g. "Confirm Received" / "Confirm Paid" */
  confirmLabel: string;
  loading: boolean;
  /** External disables (busy, sibling-field validation); an invalid date always disables. */
  disabled: boolean;
  onConfirm: (date: string) => void;
}

export function ConfirmReceivedForm({
  dateLabel,
  dateAriaLabel,
  confirmLabel,
  loading,
  disabled,
  onConfirm,
}: ConfirmReceivedFormProps) {
  const [date, setDate] = useState<string>(today);
  const [dateValid, setDateValid] = useState(true);

  return (
    <div>
      <label className={forms.label}>{dateLabel}</label>
      <div className={styles.row}>
        <DateInputNZ
          value={date}
          onChange={setDate}
          onValidityChange={setDateValid}
          inputStyles={cx(forms.input, styles.date)}
          ariaLabel={dateAriaLabel}
        />
        <Button
          onClick={() => onConfirm(date || today())}
          loading={loading}
          disabled={disabled || !dateValid}
          size="sm"
        >
          {confirmLabel}
        </Button>
      </div>
      {!dateValid && (
        <InlineBanner variant="error">
          Enter a valid date (DD/MM/YYYY)
        </InlineBanner>
      )}
    </div>
  );
}
